import React from "react";
import { useAssignees } from "../../hooks/useAssignees";

interface ActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  selectedCount: number;
  userRole: string | null;
  branchUserIds: string[];
  onConfirm: (updates: {
    assigneeId?: string;
    assigneeName?: string;
    status?: string;
  }) => Promise<void>;
}

const ActionDialog: React.FC<ActionDialogProps> = ({
  isOpen,
  onClose,
  title,
  selectedCount,
  userRole,
  branchUserIds,
  onConfirm,
}) => {
  const [selectedAssigneeId, setSelectedAssigneeId] = React.useState("");
  const [selectedStatus, setSelectedStatus] = React.useState("");
  const [isUpdating, setIsUpdating] = React.useState(false);

  const { assigneeOptions, isLoading: loadingAssignees } = useAssignees({
    userRole,
    branchUserIds,
  });

  const handleConfirm = async () => {
    if (!selectedAssigneeId && !selectedStatus) {
      alert("Please select at least one field to update.");
      return;
    }

    setIsUpdating(true);
    try {
      const updates: {
        assigneeId?: string;
        assigneeName?: string;
        status?: string;
      } = {};

      if (selectedAssigneeId) {
        const selectedOption = assigneeOptions.find(
          (opt) => opt.id === selectedAssigneeId,
        );
        updates.assigneeId = selectedAssigneeId;
        updates.assigneeName = selectedOption?.displayName || "";
      }

      if (selectedStatus) {
        updates.status = selectedStatus;
      }

      await onConfirm(updates);
      onClose();
      // Reset local state
      setSelectedAssigneeId("");
      setSelectedStatus("");
    } catch (error) {
      console.error("Error in bulk update:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white p-6 rounded-md shadow-lg max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h2 className="text-xl font-semibold">{title || "Bulk Actions"}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none cursor-pointer"
          >
            &times;
          </button>
        </div>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            You have selected{" "}
            <span className="font-semibold">{selectedCount}</span> leads.
          </p>

          <div className="space-y-1">
            <label
              htmlFor="assignee"
              className="block text-sm font-medium text-gray-700"
            >
              Set Assignee
            </label>
            <select
              name="assignee"
              id="assignee"
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={selectedAssigneeId}
              onChange={(e) => setSelectedAssigneeId(e.target.value)}
            >
              <option value="" disabled>
                {loadingAssignees ? "Loading assignees..." : "Select assignee"}
              </option>
              {assigneeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.displayName}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700"
            >
              Update Status
            </label>
            <select
              name="status"
              id="status"
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="" disabled>
                Select status
              </option>
              <option value="New">New</option>
              <option value="Unassigned">Unassigned</option>
              <option value="Contacted">Contacted</option>
              <option value="Engaging">Engaging</option>
              <option value="Potential">Potential</option>
              <option value="Enrolled">Enrolled</option>
              <option value="Cold">Cold</option>
              <option value="Lost">Lost</option>
              <option value="Duplicate">Duplicate</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isUpdating || (!selectedAssigneeId && !selectedStatus)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300 cursor-pointer disabled:cursor-not-allowed"
            >
              {isUpdating ? "Updating..." : "Apply Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionDialog;
